import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, PaginatedApiResponse } from '@card-software/nexus-utilities';
import { Greige, S3Object } from '../../models/greige/greige';
import { CommandSearchGreige } from '../../models/greige/greige-search';

@Injectable({
  providedIn: 'root',
})
export class GreigeProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.itemApi + '/api/v1/greige';

  public getAllGreiges(): Observable<ApiResponse<Greige[]>> {
    return this.httpClient.get<ApiResponse<Greige[]>>(`${this.baseUrl}/all`);
  }

  public getGreigeById(id: string): Observable<ApiResponse<Greige>> {
    return this.httpClient.get<ApiResponse<Greige>>(`${this.baseUrl}/${id}`);
  }

  public batchGetGreigesByCodes(greigeCodes: string[]): Observable<ApiResponse<Greige[]>> {
    return this.httpClient.post<ApiResponse<Greige[]>>(`${this.baseUrl}/greige-code/batch`, greigeCodes);
  }

  public createGreige(greige: Greige): Observable<ApiResponse<Greige>> {
    return this.httpClient.post<ApiResponse<Greige>>(this.baseUrl, greige);
  }

  public updateGreige(id: string, greige: Greige): Observable<ApiResponse<Greige>> {
    return this.httpClient.put<ApiResponse<Greige>>(`${this.baseUrl}/${id}`, greige);
  }

  public deleteGreige(id: string): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);
  }

  public searchGreigeAnyMatch(command: CommandSearchGreige, matchAny = true): Observable<ApiResponse<PaginatedApiResponse<Greige>>> {
    const params = new HttpParams().set('matchAny', matchAny);

    return this.httpClient.post<ApiResponse<PaginatedApiResponse<Greige>>>(`${this.baseUrl}/search`, command, { params });
  }

  public search(
    searchCommand: CommandSearchGreige,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string,
    sortOrder?: number,
    matchAny = true,
  ): Observable<ApiResponse<PaginatedApiResponse<Greige>>> {
    let url = `${this.baseUrl}/search?pageNumber=${pageNumber}&pageSize=${pageSize}&matchAny=${matchAny}`;
    if (sortField) {
      url += `&sortField=${sortField}&sortOrder=${sortOrder ?? -1}`;
    }
    return this.httpClient.post<ApiResponse<PaginatedApiResponse<Greige>>>(url, searchCommand);
  }

  public uploadKnittingMachineGraph(id: string, file: File): Observable<ApiResponse<S3Object>> {
    const formData = new FormData();

    formData.append('files', file);

    return this.httpClient.post<ApiResponse<S3Object>>(`${this.baseUrl}/upload-files/${id}`, formData, {
      responseType: 'json',
    });
  }

  public deleteKnittingMachineGraph(id: string, file: S3Object[]): Observable<ApiResponse<string>> {
    return this.httpClient
      .post<ApiResponse<string>>(`${this.baseUrl}/delete-files/${id}`, file, {
        responseType: 'json',
      })
      .pipe(tap((res) => console.log(res)));
  }
}
